import React, { useRef, useState, useEffect } from 'react';
import { Play, Heart, Volume2, VolumeX, Music2 } from 'lucide-react';
import { getReels, resolveImg } from '../lib/api';
import { BRAND } from '../data/mock';

export default function ReelsSection() {
  const [reels, setReels] = useState([]);
  const [active, setActive] = useState(null);
  const [muted, setMuted] = useState(true);
  const scrollRef = useRef(null);

  useEffect(() => { getReels().then(setReels); }, []);

  return (
    <section className="py-20 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-1/3 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="text-orange-500 text-xs uppercase tracking-[0.3em] mb-2">Reels</div>
            <h2 className="font-display text-4xl md:text-5xl">Feel The <span className="text-orange-500">Craze</span> — In Motion</h2>
            <p className="mt-3 text-white/60">Short bites of what makes Zayka Sphere unforgettable. Tap any reel to enlarge.</p>
          </div>
          <button onClick={() => setMuted(m => !m)} className="self-start md:self-end inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-white/70 hover:text-orange-400 hover:border-orange-500/60 text-xs uppercase tracking-widest">
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />} {muted ? 'Muted' : 'Sound On'}
          </button>
        </div>

        <div ref={scrollRef} className="flex gap-4 overflow-x-auto pb-3 snap-x snap-mandatory scrollbar-hide" style={{scrollbarWidth:'none'}}>
          {reels.map((r, i) => {
            const isActive = active === i;
            return (
              <button key={r.id || i} data-testid={`reel-card-${i}`} onClick={() => { window.open(r.video_url || BRAND.instagram, '_blank'); setActive(isActive ? null : i); }}
                className={`snap-start group relative shrink-0 w-[220px] md:w-[260px] aspect-[9/16] rounded-3xl overflow-hidden border transition-all ${isActive ? 'border-orange-500 scale-[1.02] shadow-2xl shadow-orange-500/40' : 'border-white/10 hover:border-orange-500/50'}`}>
                <img src={resolveImg(r.image_url)} alt={r.title} className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ease-linear ${isActive ? 'scale-125' : 'group-hover:scale-110'}`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/30 to-black/40" />
                {/* Simulated "scan line" reel indicator */}
                <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
                  <div className={`h-full bg-orange-500 ${isActive ? 'animate-[progress_6s_linear_infinite]' : 'w-0'}`} style={{width: isActive ? '100%' : '0'}} />
                </div>
                <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] uppercase tracking-widest text-white/85 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Live Vibes
                </div>
                <div className={`absolute inset-0 flex items-center justify-center transition ${isActive ? 'opacity-0' : 'opacity-100'}`}>
                  <div className="w-14 h-14 rounded-full bg-orange-500/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
                    <Play className="w-6 h-6 text-white fill-white ml-0.5" />
                  </div>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-left">
                  <div className="font-bebas text-xl tracking-wide text-white">{r.title}</div>
                  <div className="flex items-center justify-between text-xs text-white/70 mt-1">
                    <span className="text-orange-400">{r.tag}</span>
                    <span className="flex items-center gap-1"><Heart className="w-3 h-3 fill-red-500 text-red-500" /> {r.likes}</span>
                  </div>
                  <div className="flex items-center gap-1 text-[10px] text-white/50 mt-2 uppercase tracking-widest">
                    <Music2 className="w-3 h-3" /> Original Sound — @zaykasphere
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <style>{`@keyframes progress { from { transform: translateX(-100%); } to { transform: translateX(0); } }`}</style>
      </div>
    </section>
  );
}
